import React, { useEffect, useMemo, useRef, useState } from "react";
import ExpandedDreamerChartModal from "@/components/global/Chat/components/dreamerCharts/hooks/components/ExpandedDreamerChartModal";
import ScheduleModal from "@/components/global/Chat/components/ScheduleModal/ScheduleModal";
import RecurringReportsModal from "@/components/global/Chat/components/dreamerReports/RecurringReportsModal";
import { useChatReportSave } from "@/components/global/Chat/components/dreamerReports/useChatReportSave";
import { useReportModalHeader } from "@/components/global/Chat/components/dreamerReports/useReportModalHeader";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { useDeleteReportingScheduler } from "@/entities/reportingScheduler/hooks";
import { useMessageReportRow } from "@/components/global/Chat/components/dreamerCharts/useMessageReportRow";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import DataTable from "@/components/controls/DataTable/DataTable";
import DreamerAttachmentMessageCard from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentMessageCard";
import DreamerAttachmentEmptyState, {
  DreamerAttachmentEmptyVariant
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentEmptyState";
import DreamerAttachmentPreviewFooter from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentPreviewFooter";
import ChartBody from "@/components/global/Chat/components/dreamerCharts/hooks/components/ChartBody";
import {
  ChartDisplayType,
  ChartKind,
  toChartKind
} from "@/components/global/Chat/chartKind";
import {
  ChartTableViewMode,
  useChartTableViewTabOptions
} from "@/components/global/Chat/components/dreamerCharts/table/useChartTableViewTabOptions";
import { useDreamerAttachmentData } from "@/components/global/Chat/components/dreamerAttachment/useDreamerAttachmentData";
import { buildStackedChartData } from "@/components/global/Chat/components/dreamerCharts/hooks/buildStackedChartData";
import { buildLineChartData } from "@/components/global/Chat/components/dreamerCharts/hooks/buildLineChartData";
import { buildPieChartData } from "@/components/global/Chat/components/dreamerCharts/hooks/buildPieChartData";
import {
  DreamerBarChartDataType,
  DreamerLineChartDataType,
  DreamerPieChartDataType,
  DreamerStackBarChartDataType,
  LineConfig,
  StackSegmentConfig
} from "@/components/DreamerCharts/types";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import { getCurrentLocale } from "@/utils/locale";
import {
  formatNumber,
  formatPercentage,
  reducePrecision
} from "@/utils/numbers";
import { DreamerAttachmentFilterMetadata } from "@/entities/attachments/types";
import { MAX_ROWS_IN_PREVIEW } from "@/components/global/Chat/components/dreamerCharts/table/TableMessage";
import { useTranslation } from "react-i18next";
import { useScheduleConfirmation } from "@/components/global/Chat/components/dreamerReports/useScheduleConfirmation";
import {
  resolveIsSchedulable,
  useControllableExpansion
} from "@/components/global/Chat/components/dreamerCharts/useControllableExpansion";

const FALLBACK_TITLE = "Bar chart";
const MAX_CHART_BARS_IN_PREVIEW = 12;

const isMonthGranularityKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return normalizedKey === "month" || normalizedKey.endsWith("_month");
};

const formatChartLabel = (
  raw: unknown,
  displayType: string | undefined,
  columnKey?: string
): string => {
  if (raw === null || raw === undefined || raw === "") return "";
  if (displayType === FieldDisplayType.DATE) {
    const hideDay = columnKey ? isMonthGranularityKey(columnKey) : false;
    return simpleDate(toLocalDate(String(raw)), "short", false, false, hideDay);
  }
  if (displayType === ChartDisplayType.MONTH) {
    const num = typeof raw === "number" ? raw : Number(raw);
    if (Number.isInteger(num) && num >= 1 && num <= 12) {
      return new Date(2000, num - 1, 1).toLocaleString(getCurrentLocale(), {
        month: "short"
      });
    }
    return String(raw);
  }
  return String(raw);
};

interface ChartMessageProps {
  metadata: DreamerAttachmentFilterMetadata;
  messageId: string;
  isSchedulable: boolean;
  isReplayable?: boolean;
  savedReportId?: string | null;
  scheduleId: string | null;
  /**
   * What the user currently calls the saved report this message belongs to.
   * The card shows it in place of the generated title, and the download saves
   * under it — a rename writes the report row, which the chart's own title,
   * minted by the brain with the data, can never follow.
   */
  savedReportName?: string | null;
  defaultExpanded?: boolean;
  /** Controls the expanded modal, for callers that outlive one opening. */
  isExpanded?: boolean;
  onExpandedClose?: () => void;
}

const ChartMessage: React.FC<ChartMessageProps> = ({
  metadata,
  messageId,
  isSchedulable,
  isReplayable = false,
  savedReportId = null,
  scheduleId,
  savedReportName = null,
  defaultExpanded = false,
  isExpanded,
  onExpandedClose
}) => {
  const { t } = useTranslation();
  const appendConfirmation = useScheduleConfirmation();

  const [localScheduleId, setLocalScheduleId] = useState<
    string | null | undefined
  >(undefined);
  const effectiveScheduleId =
    localScheduleId !== undefined ? localScheduleId : scheduleId;

  const {
    row: reportRow,
    schedule: scheduleData,
    isRowLoading,
    isRowFailed
  } = useMessageReportRow(effectiveScheduleId, savedReportId);

  const effectiveIsSchedulable = resolveIsSchedulable(
    isSchedulable,
    isReplayable,
    scheduleId
  );

  // The report's own name wins where there is one: it is the only one of
  // the two the user can change.
  const reportName = savedReportName || metadata.title || FALLBACK_TITLE;

  const deleteScheduler = useDeleteReportingScheduler();
  const { forgetSavedReport } = useChat();
  const { forgetReportRun } = useReportRuns();
  const handleCancelSchedule = () => {
    const deletedId = scheduleData?.id;
    if (!deletedId) return;
    deleteScheduler.mutate(
      { pathParams: { id: deletedId } },
      {
        onSuccess: () => {
          setLocalScheduleId(null);
          // The row is gone, so no answer may keep pointing a save at it.
          forgetSavedReport(deletedId);
          forgetReportRun(deletedId);
          appendConfirmation({ kind: "canceled", name: reportName });
        }
      }
    );
  };
  const { isExpandedOpen, expand, setIsExpandedOpen } =
    useControllableExpansion({ isExpanded, defaultExpanded, onExpandedClose });
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const reportSave = useChatReportSave({
    messageId,
    reportName,
    savedReportId,
    isScheduled: !!scheduleData,
    scheduleConfig: scheduleData?.scheduleConfig ?? null,
    isSchedulable: effectiveIsSchedulable,
    onSchedule: () => setIsScheduleModalOpen(true),
    onScheduleCleared: () => setLocalScheduleId(null)
  });
  const modalHeader = useReportModalHeader({
    savedReportId: reportSave.savedReportId,
    savedName: reportRow?.name,
    messageId,
    isReplayable,
    // Only a view another surface controls is one that follows the report's runs.
    regeneratesInPlace: isExpanded !== undefined,
    renderSaveControl: reportSave.renderSaveControl,
    onClose: () => setIsExpandedOpen(false)
  });
  const [viewMode, setViewMode] = useState<ChartTableViewMode>("chart");
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  const {
    attachmentId,
    rows,
    totalRows,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    download,
    isDownloading,
    sorting,
    setSorting,
    filters,
    setFilters,
    hasActiveFilters,
    hasSearchTerm,
    previewColumns,
    sortableColumns,
    filterOptions
  } = useDreamerAttachmentData(metadata, {
    downloadFilenameStem: savedReportName
  });

  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    // data-id (not data-test) so the selector survives prod builds that
    // strip test-only attributes.
    const container = sentinel?.closest<HTMLElement>(
      "[data-id=chat-container]"
    );
    if (!sentinel || !container) return;

    const id = setTimeout(() => {
      const delta =
        sentinel.getBoundingClientRect().bottom -
        container.getBoundingClientRect().bottom;
      if (delta > 0) container.scrollBy({ top: delta, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(id);
  }, [viewMode]);

  const viewTabOptions = useChartTableViewTabOptions();

  const chartTitle = reportName;
  const columns = metadata.columns;
  const axisLabels = metadata.axisLabels;
  const chartKind = toChartKind(metadata.display);

  const findColumn = (key: string | null | undefined) =>
    key ? columns?.find((column) => column.key === key) : undefined;
  const labelColumn = findColumn(axisLabels?.x) ?? columns?.[0];
  const valueColumn = findColumn(axisLabels?.y) ?? columns?.[1];
  const valueDisplayType = valueColumn?.displayType;
  const isFractionalPercent = valueDisplayType === FieldDisplayType.PERCENTAGE;
  const isWholePercent =
    valueDisplayType === ChartDisplayType.PERCENTAGE_0_TO_100;
  const isPercentage = isFractionalPercent || isWholePercent;

  const chartData = useMemo<DreamerBarChartDataType[]>(() => {
    if (chartKind !== ChartKind.Bar || !labelColumn || !valueColumn) return [];
    return rows.map((row) => {
      const rawValue = row[valueColumn.key];
      const num = typeof rawValue === "number" ? rawValue : Number(rawValue);
      const numeric = Number.isFinite(num) ? num : 0;
      let value = isFractionalPercent ? numeric * 100 : numeric;
      if (isWholePercent) value = reducePrecision(value, 2);
      return {
        label: formatChartLabel(row[labelColumn.key], labelColumn?.displayType, labelColumn?.key),
        value
      };
    });
  }, [
    chartKind,
    rows,
    labelColumn?.key,
    labelColumn?.displayType,
    valueColumn?.key,
    isFractionalPercent,
    isWholePercent
  ]);

  const stacked = useMemo<{
    data: DreamerStackBarChartDataType[];
    segments: StackSegmentConfig[];
  }>(() => {
    if (chartKind !== ChartKind.StackedBar) return { data: [], segments: [] };
    return buildStackedChartData({
      rows,
      axisLabels: axisLabels ?? null,
      formatLabel: (raw) => formatChartLabel(raw, labelColumn?.displayType, labelColumn?.key)
    });
  }, [chartKind, rows, axisLabels, labelColumn?.displayType, labelColumn?.key]);

  const line = useMemo<{
    data: DreamerLineChartDataType[];
    lines: LineConfig[];
  }>(() => {
    if (chartKind !== ChartKind.Line && chartKind !== ChartKind.MultiLine) {
      return { data: [], lines: [] };
    }
    return buildLineChartData({
      rows,
      axisLabels: axisLabels ?? null,
      valueColumn,
      isFractionalPercent,
      formatLabel: (raw) => formatChartLabel(raw, labelColumn?.displayType, labelColumn?.key)
    });
  }, [
    chartKind,
    rows,
    axisLabels,
    valueColumn?.key,
    valueColumn?.label,
    isFractionalPercent,
    labelColumn?.displayType,
    labelColumn?.key
  ]);

  const pieData = useMemo<DreamerPieChartDataType[]>(() => {
    if (chartKind !== ChartKind.Pie) return [];
    return buildPieChartData({
      rows,
      axisLabels: axisLabels ?? null,
      formatLabel: (raw) => formatChartLabel(raw, labelColumn?.displayType, labelColumn?.key)
    });
  }, [chartKind, rows, axisLabels, labelColumn?.displayType, labelColumn?.key]);

  const gauge = useMemo(() => {
    const empty = {
      value: 0,
      total: 0,
      centerValue: undefined as string | undefined,
      centerLabel: undefined as string | undefined,
      tooltipLabel: undefined as string | undefined
    };
    if (chartKind !== ChartKind.Gauge) return empty;
    const column = columns?.[0];
    // Coerce server-supplied total to a finite number; non-finite values
    // signal missing data and should drop us into the gauge empty state
    // below (rather than silently rendering a blank ring).
    const rawTotal = metadata.gauge?.total;
    const total =
      typeof rawTotal === "number" && Number.isFinite(rawTotal) ? rawTotal : 0;
    const raw = column ? rows[0]?.[column.key] : undefined;
    const num = typeof raw === "number" ? raw : Number(raw);
    const value = Number.isFinite(num) ? num : 0;

    let centerValue: string | undefined;
    let centerLabel: string | undefined;
    if (Number.isFinite(num)) {
      if (column?.displayType === FieldDisplayType.PERCENTAGE) {
        centerValue = formatPercentage(value * 100);
      } else if (column?.displayType === ChartDisplayType.PERCENTAGE_0_TO_100) {
        centerValue = `${reducePrecision(value, 2)}%`;
      } else {
        centerValue = formatNumber(value, 1, false, false).value;
        centerLabel = t("chat.outOfTotal", {
          totalNum: formatNumber(total, 1, false, false).value
        });
      }
    }
    return {
      value,
      total,
      centerValue,
      centerLabel,
      tooltipLabel: column?.label
    };
  }, [chartKind, rows, columns, metadata.gauge?.total]);

  const previewChartData = chartData.slice(0, MAX_CHART_BARS_IN_PREVIEW);
  const previewStackedData = stacked.data.slice(0, MAX_CHART_BARS_IN_PREVIEW);
  const previewLineData = line.data.slice(0, MAX_CHART_BARS_IN_PREVIEW);
  const previewPieData = pieData.slice(0, MAX_CHART_BARS_IN_PREVIEW);
  const chartPointsByKind: Record<ChartKind, number> = {
    [ChartKind.StackedBar]: stacked.data.length,
    [ChartKind.Line]: line.data.length,
    [ChartKind.MultiLine]: line.data.length,
    [ChartKind.Pie]: pieData.length,
    [ChartKind.Gauge]: 1,
    [ChartKind.Bar]: chartData.length
  };
  const totalChartPoints = chartPointsByKind[chartKind];

  // Gauge is single-row but useless without a positive total — a `total <= 0`
  // payload would otherwise render an empty ring with no message.
  const isGaugeWithoutTotal = chartKind === ChartKind.Gauge && gauge.total <= 0;

  const emptyVariant: DreamerAttachmentEmptyVariant | null = error
    ? "error"
    : isLoading
      ? null
      : rows.length > 0 && !isGaugeWithoutTotal
        ? null
        : hasActiveFilters
          ? "filters"
          : hasSearchTerm
            ? "search"
            : "noData";

  const showChartFooter =
    !emptyVariant &&
    viewMode === "chart" &&
    totalChartPoints > MAX_CHART_BARS_IN_PREVIEW;
  const showTableFooter =
    !emptyVariant && viewMode === "table" && totalRows > MAX_ROWS_IN_PREVIEW;
  const previewFooter = showChartFooter ? (
    <DreamerAttachmentPreviewFooter
      shown={MAX_CHART_BARS_IN_PREVIEW}
      total={totalChartPoints}
      unit={
        chartKind === ChartKind.Pie
          ? "slices"
          : chartKind === ChartKind.Line || chartKind === ChartKind.MultiLine
            ? "points"
            : "bars"
      }
      onViewAll={expand}
    />
  ) : showTableFooter ? (
    <DreamerAttachmentPreviewFooter
      shown={MAX_ROWS_IN_PREVIEW}
      total={totalRows}
      unit="rows"
      onViewAll={expand}
    />
  ) : undefined;

  return (
    <div className="w-full">
      <DreamerAttachmentMessageCard
        title={chartTitle}
        viewTabOptions={viewTabOptions}
        viewMode={viewMode}
        onViewModeChange={(v) => setViewMode(v as ChartTableViewMode)}
        tabsDataTest="chart-message-view-tabs"
        onExpand={expand}
        expandTestId="expand-chart-button"
        onDownload={download}
        isDownloading={isDownloading}
        canDownload={!!attachmentId}
        onSchedule={() => setIsScheduleModalOpen(true)}
        onCancelSchedule={handleCancelSchedule}
        onManageSchedules={() => setIsManageModalOpen(true)}
        scheduleData={scheduleData}
        isSchedulable={effectiveIsSchedulable}
        saveControl={reportSave.saveControl}
        footer={previewFooter}
        modal={
          <>
            <ExpandedDreamerChartModal
              isOpen={isExpandedOpen}
              setIsOpen={setIsExpandedOpen}
              title={modalHeader.title ?? chartTitle}
              headerSlots={modalHeader.headerSlots}
              chartKind={chartKind}
              chartData={chartData}
              stackedChartData={stacked.data}
              stackSegments={stacked.segments}
              lineChartData={line.data}
              lineConfigs={line.lines}
              pieChartData={pieData}
              gaugeValue={gauge.value}
              gaugeTotal={gauge.total}
              gaugeCenterValue={gauge.centerValue}
              gaugeCenterLabel={gauge.centerLabel}
              gaugeTooltipLabel={gauge.tooltipLabel}
              isPercentage={isPercentage}
              xAxisLabel={labelColumn?.label}
              yAxisLabel={valueColumn?.label}
              generatedAt={metadata.generatedAt}
              onDownload={download}
              isDownloading={isDownloading}
              canDownload={!!attachmentId}
              tableColumns={sortableColumns}
              tableData={rows}
              tableIsLoading={isLoading}
              tableHasMore={hasNextPage && !error}
              tableIsLoadingMore={isFetchingNextPage}
              onTableLoadMore={fetchNextPage}
              tableSorting={sorting}
              onTableSortingChange={setSorting}
              filters={filters}
              setFilters={setFilters}
              filterOptions={filterOptions}
              emptyVariant={emptyVariant}
            />
            <ScheduleModal
              isOpen={isScheduleModalOpen}
              setIsOpen={setIsScheduleModalOpen}
              messageId={messageId}
              reportName={reportName}
              savedReportId={reportSave.savedReportId}
              existingScheduleData={reportRow}
              isReportRowLoading={isRowLoading}
              isReportRowFailed={isRowFailed}
              onCreated={(created) => {
                setLocalScheduleId(created.id);
                reportSave.markScheduled(created.id);
              }}
              onCanceled={() => {
                reportSave.markUnscheduled();
                setLocalScheduleId(null);
                appendConfirmation({ kind: "canceled", name: reportName });
              }}
            />
            <RecurringReportsModal
              isOpen={isManageModalOpen}
              setIsOpen={setIsManageModalOpen}
            />
          </>
        }
      >
        {viewMode === "chart" ? (
          <div className="px-6 pb-6">
            <ChartBody
              chartKind={chartKind}
              height={220}
              emptyVariant={emptyVariant}
              isPercentage={isPercentage}
              chartData={previewChartData}
              xAxisLabel={labelColumn?.label}
              yAxisLabel={valueColumn?.label}
              stackedChartData={previewStackedData}
              stackSegments={stacked.segments}
              lineChartData={previewLineData}
              lineConfigs={line.lines}
              pieChartData={previewPieData}
              gaugeValue={gauge.value}
              gaugeTotal={gauge.total}
              gaugeCenterValue={gauge.centerValue}
              gaugeCenterLabel={gauge.centerLabel}
              gaugeTooltipLabel={gauge.tooltipLabel}
              compact
            />
          </div>
        ) : (
          <DataTable
            columns={previewColumns}
            data={rows.slice(0, MAX_ROWS_IN_PREVIEW)}
            isDataLoading={isLoading}
            headerClassName="border-b border-border-border1 bg-surface-elevation2"
            className="w-full max-h-[300px]"
            customEmptyState={
              emptyVariant ? (
                <DreamerAttachmentEmptyState variant={emptyVariant} />
              ) : undefined
            }
          />
        )}
      </DreamerAttachmentMessageCard>
      <div ref={bottomSentinelRef} aria-hidden />
    </div>
  );
};

export default ChartMessage;
